/**
 * SOLID Review
 * S — Strong. One concern: the legal side's decisions — the re-acceptance gate and cookie consent — each answered from
 *     its value object (`LegalReview`) or small aggregate (`CookieConsent`) through a `ClientDecision`. The rules are
 *     theirs.
 * O — Good. A new legal decision is a method here, a row in the table, and its rule on the value object.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. One role, `IServiceDecisionTable`: the Facade sees the legal ops' table and nothing else.
 * D — Strong. The legal value objects, the decision builder and the schemas.
 */
import { LegalReview } from '../../legal/LegalReview';
import { CookieConsent } from '../../consent/CookieConsent';
import { ClientDecision, type TClientDecision } from '../ClientDecision';
import type { IServiceDecisionTable, TDecisionRows } from '../IServiceDecisionTable';
import type { IPropsClientInput } from '../../ports/schemas/ClientChanges';
import type {
  ICommandPlanLegalReview, IEntityPlanLegalReview, ICommandLegalReviewOf, IEntityLegalReview, ICommandLegalAcceptance,
  IEntityLegalAcceptance,
} from '../../ports/schemas/LegalMoves';
import type {
  ICommandConsentChanged, IEntityConsentRecord, ICommandCookieConsentRecord, IEntityCookieConsentRecord,
} from '../../ports/schemas/ConsentMoves';

/** The ops this service answers for on the client Domain's lollipop. */
type TLegalOp = 'planLegalReview' | 'legalReviewOf' | 'planLegalAcceptance' | 'consentChanged' | 'planCookieConsentRecord';

/**
 * THE LEGAL SIDE (frontend-domain-oo P6, 2026-10-26; handbook §28.273) — the decide functions of
 * `decisions/legal/legalReview.ts` (FE4a, ConsentReacceptanceGate's) and `decisions/legal/cookieConsent.ts` (FE4b,
 * CookieConsentProvider's) as a service. Only the CMP's report changes the session; the gate's dialog and the audit
 * record are answers, nothing kept.
 */
export class Legal implements IServiceDecisionTable<TLegalOp> {
  /** Its decisions, by op: what `ClientDecisions` merges and answers through. */
  readonly decisions: TDecisionRows<TLegalOp> = {
    planLegalReview: (_input, command) => this.planLegalReview(command),
    legalReviewOf: (_input, command) => this.legalReviewOf(command),
    planLegalAcceptance: (_input, command) => this.planLegalAcceptance(command),
    consentChanged: (input, command) => this.consentChanged(input, command),
    planCookieConsentRecord: (_input, command) => this.planCookieConsentRecord(command),
  };

  /** The consent status: fetch the documents only when a consent is stale. */
  planLegalReview(command: ICommandPlanLegalReview): TClientDecision<IEntityPlanLegalReview> {
    const plan: IEntityPlanLegalReview = { fetchDocuments: LegalReview.isDue(command.status) };
    return ClientDecision.unchanged().answering(plan);
  }

  /** The documents to re-accept. */
  legalReviewOf(command: ICommandLegalReviewOf): TClientDecision<IEntityLegalReview> {
    const review: IEntityLegalReview = { review: LegalReview.of(command.documents).shown() };
    return ClientDecision.unchanged().answering(review);
  }

  /** Accepting the documents shown. */
  planLegalAcceptance(command: ICommandLegalAcceptance): TClientDecision<IEntityLegalAcceptance> {
    const plan: IEntityLegalAcceptance = { consents: LegalReview.of(command.review).consents() };
    return ClientDecision.unchanged().answering(plan);
  }

  /** The CMP reported a state: kept, and recorded if the server has not heard of it. */
  consentChanged(input: IPropsClientInput, command: ICommandConsentChanged): TClientDecision<IEntityConsentRecord> {
    const consent = CookieConsent.of(input.session.consent);
    const dto: IEntityConsentRecord = { record: consent.reported(command.state) };
    return ClientDecision.keeping(input, consent).answering(dto);
  }

  /** The audit record of a decision. */
  planCookieConsentRecord(command: ICommandCookieConsentRecord): TClientDecision<IEntityCookieConsentRecord> {
    const dto: IEntityCookieConsentRecord = { request: CookieConsent.recordOf(command) };
    return ClientDecision.unchanged().answering(dto);
  }
}
